import { HttpClient } from '@angular/common/http';
import { from, map, switchMap, catchError, of, Observable, throwError } from 'rxjs';
import { AuthHeaderService } from './auth-header.service';
import { SanctumService } from './sanctum.service';
import { environment } from 'src/environments/environment';
import { HttpParams } from '@angular/common/http';

import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class GoogleGalleryService {

  constructor(
    private http: HttpClient,
    private authHeader: AuthHeaderService,
    private sanctum: SanctumService
  ) { 
  }

  private baseUrl() {
    return `${environment.apiBaseUrl}/api`;
  }
  private storeId(): number {
    return Number(localStorage.getItem('ss_SelectedStoreId') || 0);
  }

  getAccounts() {
    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/google/accounts`;
    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, opts)),
      map(res => Array.isArray(res.accounts) ? res.accounts : []),
      catchError((err) => {
        return of({
          items: [],
          nextPageToken: null,
          error: err?.error?.message || err.message || 'Erro desconhecido'
        });
      })
    );
  }

  getYoutubeChannels(accountId: string) {
    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/google/account/${accountId}/youtube/channels`;
    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, opts)),
      map(res => Array.isArray(res.channels) ? res.channels : [])
    );
  }

  // Busca vídeos do YouTube por hashtag
  searchYoutubeByHashtag(accountId: string, hashtag: string, mediaTypes: string[], pageToken?: string, maxResults = 50) {
    const params: any = {
      hashtag,
      media_types: mediaTypes,
      max_results: maxResults
    };
    if (pageToken) params.page_token = pageToken;

    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/google/account/${accountId}/youtube/hashtag`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, { ...opts, params })),
      map(res => ({
        items: Array.isArray(res.items) ? res.items : [],
        nextPageToken: res.nextPageToken || null
      })),
      catchError((err) => {
        return of({
          items: [],
          nextPageToken: null,
          error: err?.error?.message || err.message || 'Erro desconhecido'
        });
      })
    );
  }


  extractYoutubeByUrlPublic(urlToExtract: string, maxResults = 10) {
    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/youtube/extract`;
    const params: any = { url: urlToExtract, max_results: maxResults };

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, { ...opts, params })),
      map(res => ({
        items: Array.isArray(res.items) ? res.items : [],
        nextPageToken: null
      }))
      // catchError REMOVIDO: deixe o erro subir para o subscribe
    );
  }

  getYoutubeChannelUploads(accountId: string, channelId: string, mediaTypes: string[], pageToken?: string, maxResults = 50) {
    const params: any = {
      media_types: mediaTypes,
      max_results: maxResults
    };
    if (pageToken) params.page_token = pageToken;

    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/google/account/${accountId}/youtube/channel/${channelId}/uploads`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, { ...opts, params })),
      map(res => ({
        items: Array.isArray(res.items) ? res.items : [],
        nextPageToken: res.nextPageToken || null
      })),
      catchError((err) => {
        return of({
          items: [],
          nextPageToken: null,
          error: err?.error?.message || err.message || 'Erro desconhecido'
        });
      })
    );
  }



  // SSAPP-8: este é o único dos 6 métodos de busca do YouTube que lança
  // exceção em vez de resolver com {items:[], error}. NÃO "corrigir" isso pra
  // ficar igual aos outros 5 sem checar antes o componente que chama cada um:
  // GoogleGalleryComponent.loadChannelVideos() (caller deste método) tem um
  // handler `error:` de verdade que chama errorHandler.handle() com a
  // mensagem real do backend — os outros 5 callers (getYoutubeChannelUploads
  // etc.) NÃO checam o campo `error` do resultado resolvido em lugar nenhum,
  // só mostram "Nenhum conteúdo encontrado" genérico, igual a uma busca vazia
  // de verdade. Ou seja, é este método que está certo; os outros 5 é que
  // engolem erro real silenciosamente. Corrigir a assimetria "pra ficar
  // igual" pioraria as coisas. Ver achado documentado na SSAPP-8.
  getYoutubeChannelVideos(accountId: string, channelId: string, mediaTypes: string[], pageToken?: string, maxResults = 50) {
    const params: any = {
      media_types: mediaTypes,
      max_results: maxResults
    };
    if (pageToken) params.page_token = pageToken;

    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/google/account/${accountId}/youtube/channel/${channelId}/videos`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, { ...opts, params })),
      map(res => ({
        items: Array.isArray(res.items) ? res.items : [],
        nextPageToken: res.nextPageToken || null
      })),
      catchError((err) => throwError(() => err))
    );
  }
  ///////////////////////////////////////
  getYoutubePlaylistItems(
    accountId: string,
    playlistId: string,
    mediaTypes: string[] = [],
    pageToken?: string,
    maxResults = 50
  ) {
    const params: any = { max_results: maxResults, media_types: mediaTypes };
    if (pageToken) params.page_token = pageToken;

    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/google/account/${accountId}/youtube/playlist/${playlistId}/items`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, { ...opts, params })),
      map(res => ({
        items: Array.isArray(res.items) ? res.items : [],
        nextPageToken: res.nextPageToken || null
      })),
      catchError(err => of({ items: [], nextPageToken: null, error: err?.error?.message || err.message }))
    );
  }
  getYoutubeChannelPlaylists(
    accountId: string,
    channelId: string,
    pageToken?: string,
    maxResults = 10,
    q?: string // opcional: buscar por nome
  ) {
    const params: any = { max_results: maxResults };
    if (pageToken) params.page_token = pageToken;
    if (q) params.q = q;

    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/google/account/${accountId}/youtube/channel/${channelId}/playlists`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, { ...opts, params })),
      map(res => ({
        items: Array.isArray(res.items) ? res.items : [],
        nextPageToken: res.nextPageToken || null
      })),
      catchError(err => of({ items: [], nextPageToken: null, error: err?.error?.message || err.message }))
    );
  }

  getYoutubeMyPlaylists(
    accountId: string,
    pageToken?: string,
    maxResults = 10
  ) {
    const params: any = { max_results: maxResults };
    if (pageToken) params.page_token = pageToken;

    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/google/account/${accountId}/youtube/playlists/mine`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, { ...opts, params })),
      map(res => ({
        items: Array.isArray(res.items) ? res.items : [],
        nextPageToken: res.nextPageToken || null
      })),
      catchError(err => of({ items: [], nextPageToken: null, error: err?.error?.message || err.message }))
    );
  }


  ///////////////////////////



  getYoutubeLikedVideos(
    accountId: string,
    channelId: string,
    mediaTypes: string[],
    pageToken?: string,
    maxResults = 50
  ) {
    const params: any = {
      media_types: mediaTypes,
      max_results: maxResults
    };
    if (pageToken) params.page_token = pageToken;

    const url = `${this.baseUrl()}/admin/store/${this.storeId()}/google/account/${accountId}/youtube/channel/${channelId}/liked`;

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeader.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, { ...opts, params })),
      map(res => ({
        items: Array.isArray(res.items) ? res.items : [],
        nextPageToken: res.nextPageToken || null
      })),
      catchError((err) => {
        return of({
          items: [],
          nextPageToken: null,
          error: err?.error?.message || err.message || 'Erro desconhecido'
        });
      })
    );
  }


}
